import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment-timezone';
import * as React from 'react';

import { DateTimePicker } from '../../components/TimePicker';
import { checkoutInstantIso, checkoutPickerMoment, tripDateMidnight } from './checkoutTime';

/**
 * The booking's own check-out, picked rather than typed (CP-8385).
 *
 * The typed field walked "MMMM DD, YYYY hh:mm aa" section by section, month
 * name included, and a half-typed value was easy to leave behind. This is the
 * date/time picker from Imports > All Imports: clicking anywhere on the field
 * opens the calendar and the hour / minute / AM-PM columns.
 *
 * `value` and `onChange` speak the stored ISO instant; the picker shows it on
 * the tour's clock (CP-8377). There is no Clear: unticking "This booking has
 * its own check-out time" is how a booking goes back to the tour length.
 *
 * `tripDate` ('YYYY-MM-DD') is where the calendar opens while no check-out is
 * set yet, rather than on today (CP-8392).
 */
const CheckoutPicker = ({ value, onChange, timeZone, tripDate, disabled, textFieldProps }) => {
    const [open, setOpen] = React.useState(false);
    const referenceDate = tripDateMidnight(tripDate, timeZone);

    return (
        <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
            <DateTimePicker
                timezone={timeZone}
                value={checkoutPickerMoment(value, timeZone)}
                {...(referenceDate ? { referenceDate } : {})}
                onChange={(next) => onChange(checkoutInstantIso(next))}
                disabled={disabled}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                format="LLL"
                label="Check-out (this booking)"
                slotProps={{
                    // 'LLL' writes 3:45 PM, as the Users grid does; without this the field pads it to 03:45.
                    field: { readOnly: true, shouldRespectLeadingZeros: true },
                    textField: {
                        variant: 'filled',
                        fullWidth: true,
                        // Not a limit: a hotel stay can run past the tour. "not after the tour length" read as one.
                        helperText: `Tour timezone (${timeZone}). Used instead of the end worked out from the tour length.`,
                        onClick: () => !disabled && setOpen(true),
                        ...textFieldProps,
                    },
                    actionBar: { actions: ['accept'] },
                }}
            />
        </LocalizationProvider>
    );
};

export default CheckoutPicker;
